import React from "react";
import { HelpCircle } from "lucide-react";
import { Card } from "@/components/Card/Card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AverageSaleValueMetrics } from "../../api/fetchROIDashboardData/types";
import { formatCurrency } from "@/modules/stochastic/features/DashboardPage/utils/chart";

interface AverageSaleValueCardProps {
  averageSaleValue: AverageSaleValueMetrics | null;
}

export const AverageSaleValueCard: React.FC<AverageSaleValueCardProps> = ({
  averageSaleValue,
}) => {
  const currentValue = averageSaleValue?.currentValue;
  const changeFromLastMonth = averageSaleValue?.changeFromLastMonth;

  const formatCurrencyValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return "N/A";
    }
    return formatCurrency(value);
  };

  const isPositiveChange =
    changeFromLastMonth !== null &&
    changeFromLastMonth !== undefined &&
    changeFromLastMonth > 0;
  const isNegativeChange =
    changeFromLastMonth !== null &&
    changeFromLastMonth !== undefined &&
    changeFromLastMonth < 0;

  return (
    <Card className="w-full">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Average Sale Value
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  The average dollar amount of each sale. Higher values mean
                  customers are purchasing more expensive services or multiple
                  services at once.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="text-3xl font-bold text-gray-900 dark:text-white">
          {formatCurrencyValue(currentValue)}
        </div>

        {changeFromLastMonth !== null && changeFromLastMonth !== undefined && (
          <div
            className={`flex items-center gap-1 mt-1 text-sm ${
              isPositiveChange
                ? "text-green-600"
                : isNegativeChange
                  ? "text-red-600"
                  : "text-gray-500"
            }`}
          >
            <span>
              {isPositiveChange ? "\u2191" : isNegativeChange ? "\u2193" : ""}{" "}
              {formatCurrency(Math.abs(changeFromLastMonth))} from last month
            </span>
          </div>
        )}
      </div>
    </Card>
  );
};
